import Wrapper from '@/common/wrapper/Wrapper';
import style from './style/ExamList.module.less';
import {Input, Pagination, Button, Cascader, message} from 'antd';
import { gradeRecord, courseRecord, getCourseByGrade, getGradeBySchoolType } from '@/utils/const';
import { apiRemoveExam, examArchive, getExamList, switchSchoolImport } from '@/service/exam/list';
import {usePageState, useRouter, useUserInfo} from '@/hooks/hooks';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import IconFont from '@/common/IconFont';
import {getTermDetail, getTermList} from '@/service/common';
import ExamItem from './components/ExamItem';
import LoadingWrapper from '@/common/loadingWrapper/LoadingWrapper';
import Empty from '@/common/empty/Empty';
import layer from '@/utils/layer';
import { debounce } from 'lodash';
import {formatSearchParams, hasPermission} from '@/utils';
import { useActivate } from 'react-activation';

interface Option {
  value: string | number;
  label: string;
  children?: Option[];
}

const ExamCenter = () => {

  const state = usePageState<Exam, GetExamListParams>({
    termDetailId: 1,
    gradeDcode: '',
    examNameLike: '',
    coursetypeDcode: ''
  });
  const loadFn = state.loadTableWithPage(getExamList);

  const [termList, setTermList] = useState<Option[]>([]);
  const currentTerm = useRef<(string | number)[]>([]);

  const userState = useUserInfo();

  // 科目·
  const courseList = useMemo(() => {
    return getCourseByGrade(
      state.params.gradeDcode || (userState.userType === 'EDU' ? '' : userState.currentSchool.schoolgradeDcode)
    ) || [];
  }, [state.params.gradeDcode]);

  // 年级
  const gradeList = useMemo(() => {
    if (userState.userType === 'EDU') return gradeRecord;
    else {
      return getGradeBySchoolType(userState.currentSchool.schoolgradeDcode);
    }
  }, [userState.userType]);

  // 获取当前学期
  const getCurrentTerm = (list: Term[]) => {
    if (list.length === 0) {
      throw new Error('当前没有学期');
    }
    const currentTime = new Date().getTime();
    const arr = list.map((i) => {
      return {
        ...i,
        startDt: new Date(i.startDt).getTime(),
        endDt: new Date(i.endDt).getTime()
      };
    });
    const length = arr.length;
    for (let i = 0; i < length; i++) {
      if (currentTime >= arr[i].startDt && currentTime <= arr[i].endDt) {
        return arr[i];
      } else if (currentTime >= arr[i].startDt && currentTime <= arr[i + 1]?.endDt) {
        return arr[i + 1];
      }
    }
    // 时间在所有学期范围之外
    // 判断在之前还是之后
    return arr[currentTime < arr[0].startDt ? 0 : length - 1];
  };

  const loadTermList = async () => {
    const res = await getTermList();
    let arr: Option[] = res.data.map(el => {return {value: el.id, label: el.name + '学年', children: []};});
    const { data } = await getTermDetail();
    data.forEach(el => {
      const find = arr.find(al => al.value === el.termId);
      if (find) {
        find.children?.push({
          value: el.id,
          label: el.name
        });
      }
    });
    const current = getCurrentTerm(data);
    state.params.termDetailId = current.id;
    setTermList(arr);
    currentTerm.current = [current.termId, current.id];
  };

  const isEduUser = useMemo(() => userState.userType === 'EDU', [userState]);

  useEffect(() => {
    sessionStorage.removeItem('absenceTab');
    loadTermList().then(() => loadFn());
  }, []);

  useActivate(() => {
    loadFn();
  });

  const paramsChange = (value: string | number, type: string, search = true) => {
    const newParams = {
      ...state.params,
      pageNum: search ? 1 : state.params.pageNum,
      [type]: value
    };
    state.setParams(newParams);
    search && loadFn(newParams);
  };

  const paramsChangeTerm = (value: (string| number)[]) => {
    currentTerm.current = value;
    paramsChange(value[1], 'termDetailId');
  };

  const gradeChange = (gradeDcode: string) => {
    const newParams = {
      ...state.params,
      pageNum: 1,
      gradeDcode
    };
    const c = getCourseByGrade(gradeDcode);
    if (!c?.includes(state.params.coursetypeDcode as string)) {
      newParams.coursetypeDcode = '';
    }
    state.setParams(newParams);
    loadFn(newParams);
  };

  const inputChangeSearch = useCallback(debounce((params: GetExamListParams) => {
    loadFn(params);
  }, 300), []);

  const inputChange = (value: string) => {
    const newParams = {
      ...state.params,
      pageNum: 1,
      examNameLike: value
    };
    state.setParams(newParams);
    inputChangeSearch(newParams);
  };

  const pageChange = (pageNum: number, pageSize: number) => {
    const newValue = {
      ...state.params,
      pageNum,
      pageSize
    };
    state.setParams(newValue);
    loadFn(newValue);
  };

  const router = useRouter();

  const toExamAdd = (prop?: Exam) => {
    if (!prop) {
      router.to('/front/examCenter/examAdd');
    } else {
      router.to(`/front/examCenter/examEdit?examId=${prop.id}&examName=${prop.name}`);
    }
  };

  const deleteFn = (prop: Exam) => {
    layer.confirm({
      content: '确定删除该考试？',
      onOk: async () => {
        await apiRemoveExam(prop.id);
        loadFn();
      }
    });
  };
  // 缺考标记页面跳转
  const toExamAbsence = (prop: Exam) => {
    if (userState.userType === 'TEACHER') {
      router.to('/front/examCenter/examAbsence/' + router.encodeParams(`${userState.currentSchool.id}+${userState.currentSchool.name}`) + `?examName=${prop.name}&examId=${prop.id}&archive=${prop.archive}`);
    } else {
      router.to(`/front/examCenter/examAbsence?examName=${prop.name}&examId=${prop.id}&archive=${prop.archive}`);
    }
  };

  // 去导入成绩页面
  const toImportScorePage = (p: ExamCours, exam: Exam) => {
    // router.to(
    //   router.encodeParams(`${exam.id}+${exam.name}`) + '/' +
    //   router.encodeParams(`${p.id}+导入成绩-${courseRecord[p.coursetypeDcode]}`) +
    //   `?courseDcode=${p.coursetypeDcode}&fullScore=${p.fullScore}`);
    router.to(
      '/front/examCenter/' +
      router.encodeParams(`${p.id}+导入成绩-${courseRecord[p.coursetypeDcode]}`) +
      `?examName=${exam.name}&courseDcode=${p.coursetypeDcode}&hasReport=${exam.hasGenerateReport}&archive=${exam.archive}&examId=${p.examId}`
    );
  };

  // 去报告设置页面
  const toReportSettingPage = (exam: Exam) => {
    router.to('reportSetting',
      {
        gradeDcode: exam.gradeDcode,
        examid: exam.id,
        hasScore: exam.existScore,
        archive: exam.archive
      },
      {
        state: exam
      }
    );
  };

  const checkDataScope = (exam: Exam) => {
    if (userState.userType !== 'EDU' || Object.keys(userState.eduDataScope).length === 0) return true;
    return Boolean(userState.eduDataScope[exam.gradeDcode[0]] &&
        userState.eduDataScope[exam.gradeDcode[0]].some(el => exam.examCoursetypeDcodes.includes(el) || el === 'WHOLE_COURSE_T'));
  };

  // 查看分析页面
  const toAnalysisPage = (exam: Exam) => {
    if (!checkDataScope(exam)) {
      message.warn('暂无查看分析权限');
      return;
    }
    const searchParams = {
      examid: exam.id,
      gradeDcode: exam.gradeDcode,
      name: exam.name
    };
    window.open('/front/analysis' + formatSearchParams(searchParams));
    // router.to('/front/analysis/allCourseAvg', {
    //   examid: exam.id,
    //   gradeDcode: exam.gradeDcode,
    //   name: exam.name,
    //   courseDcodes: exam.examCourses.map(i => i.coursetypeDcode).toString()
    // });
  };

  // 修改成绩页面
  const toModifyScorePage = (exam: Exam) => {
    window.open('/front/examCenter/' + router.encodeParams(`${exam.id}+${exam.name}`) + `/modifyScore?archive=${exam.archive}`);
  };

  // 发布设置
  const toPublish = (exam: Exam) => {
    router.to(`/front/examCenter/publish?examId=${exam.id}`);
  };

  // 去各校导入概率
  const toSchoolImportedPage = (prop: Exam) => {
    router.to(`schoolImportedPreview?examName=${prop.name}&examId=${prop.id}`);
  };

  // 点击归档
  const acrhive = (prop: Exam) => {
    layer.confirm({
      content: '归档后，学生信息调整将不影响报告数据',
      okText: '归档',
      onOk: async () => {
        const { data } = await examArchive(prop.id);
        data && layer.info('归档成功');
        loadFn();
      }
    });
  };

  // 学校是否可以导入开关
  const canSchoolImportChange = async (prop: SwitchSchoolImportParams) => {
    const { data, msg } = await switchSchoolImport(prop);
    if (data) {
      const list = state.list.map(i => {
        if (i.id === prop.examId) {
          return {
            ...i,
            canSchoolImport: prop.isOpen,
            schoolImportEndTime: prop.endTime
          };
        } else {
          return i;
        }
      });
      state.setList(list);
      layer.info('设置成功');
    } else {
      layer.error(msg);
    }
  };

  return (
    <div className={style.examListWrapper}>
      {
        isEduUser && hasPermission('EXAM:ADD_UPDATE') &&
          (<Button className={style.addExamBtn} onClick={() => toExamAdd()} type="primary" icon={<IconFont style={{color: '#fff'}} type="icon-tianjia" />}>创建考试</Button>)
      }
      <Wrapper>
        <div className={style.searchPanel}>
          <Cascader value={currentTerm.current} options={termList} onChange={paramsChangeTerm} allowClear={false} />
          <Input
            value={state.params.examNameLike}
            onChange={e => inputChange(e.target.value)}
            placeholder="输入考试名称"
            suffix={<IconFont type="icon-sousuo" />}
          />
        </div>
        <div className={style.pickerWrapper}>
          <span>年级：</span>
          <div className={style.itemWrapper}>
            {
              [['', '全部'], ...Object.entries(gradeList)].map(([key, value]) => (
                <span
                  key={key}
                  className={state.params.gradeDcode === key ? style.active: ''}
                  onClick={() => gradeChange(key)}
                >
                  {value}
                </span>
              ))
            }
          </div>
        </div>
        <div className={style.pickerWrapper}>
          <span>科目：</span>
          <div className={style.itemWrapper}>
            {
              ['', ...courseList].map((key) => (
                <span
                  key={key}
                  className={state.params.coursetypeDcode === key ? style.active: ''}
                  onClick={() => paramsChange(key, 'coursetypeDcode')}
                >
                  {courseRecord[key] || '全部'}
                </span>
              ))
            }
          </div>
        </div>
      </Wrapper>
      {/* 列表 */}
      <LoadingWrapper loading={state.loading}>
        {
          state.list.map((i, index) => (
            <ExamItem
              key={i.id}
              {...i}
              toExamAdd={toExamAdd}
              deleteFn={deleteFn}
              toAbsence={toExamAbsence}
              toImportScorePage={e => toImportScorePage(e, i)}
              toReportSettingPage={() => toReportSettingPage(i)}
              toAnalysisPage={() => toAnalysisPage(i)}
              toModifyScorePage={toModifyScorePage}
              toPublish={toPublish}
              index={index}
              onArchive={acrhive}
              toSchoolImportedPage={toSchoolImportedPage}
              canSchoolImportChange={canSchoolImportChange}
            />
          ))
        }
        {
          state.list.length === 0 && <Empty style={{marginTop: '20px'}}></Empty>
        }
        <div className={style.page}>
          <div>共<span>{state.total}</span>条记录</div>
          <Pagination
            current={state.params.pageNum}
            pageSize={state.params.pageSize}
            total={state.total}
            showQuickJumper
            showSizeChanger
            pageSizeOptions={[10, 20, 50]}
            onChange={pageChange}
          />
        </div>
      </LoadingWrapper>
    </div>
  );
};

export default ExamCenter;
